import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import ReportTableView from "./ReportTableView";
import ExportToCSV from "./ExportToCSV";
import { useTableWorker } from "../../hooks/useTableWorker";
import DocumentLibrary from "./DocumentLibrary";

function ReportViewData({
    selectedReportDetails,
    selectedReportView,
    dataViewType,
    allReportViewsData,
    organizationName,
    relatedOrganizationNames,
    reportTableDisplayInformation,
    handleDropdownSelectedReport,
    reportDropdownOptions,
    dropdownSelectedReport,
    reportTableData,
    enableSaveButton,
    setEnableSaveButton,
    page,
    setPage,
    clearColumnFilters,
    isFetchingReportData,
    isFetchingAccommodation,
    subviewName,
    subviewMap,
    isErrorReport,
    organizationsAccess,
    reportDescription,
    disclaimer,
    reportLastFetched,
    userEmailID,
    totalResults,
    retrievedCount,
    isPaginating,
    isError,
    //
    selectedCoverageType,
    selectedReportingGroup,
    selectedPeriodScheduleType,
    selectedLeaveScheduleType,
    selectedStatus,
    selectedPeriodStatus,
    selectedProtectionStatus,
    selectedIncludeHistoricalLeave,
    selectedReportingPeriod,
    selectedPolicyDivision,
    selectedPaymentStatus,
    //
    enableCoverageType,
    enableStatusFilter,
    enablePeriodScheduleTypeFilter,
    enableLeaveScheduleTypeFilter,
    enablePeriodStatusFilter,
    enableProtectionStatusFilter,
    enableIncludeHistoricalLeave,
    enableReportingPeriodFilter,
    enableReportGroupFilter,
    enablePolicyandDivisisonFilter,
    enablePaymentStatus,
    hiddenFilterValues,
    handleViewReportSave,
    loaderStatus,
    notificationStatus,
    allViewsData,
    handleViewReportUpdate,
    leavesStatus,
    selectedApplyingReportingPeriod,
    enableApplyReportingPeriodFilter,
    HighVolumeReport,
}) {
    const exportRef = useRef();

    const columnsDatas =
        selectedReportDetails?.TableDisplayInformation?.ColumnInformation;

    let getColumnLevelFilters = (filters) => {
        if (filters?.length) {
            return filters
                .map((savedFilter) => {
                    const columnInfo = reportTableDisplayInformation.find(
                        (col) => col.columnId === savedFilter.columnFilterId
                    );

                    if (columnInfo) {
                        return {
                            columnName: columnInfo.header,
                            accessorKey: columnInfo.accessorKey,
                            columnId: columnInfo.columnId,
                            filterValue: savedFilter.filterValue,
                        };
                    }
                    return null;
                })
                .filter(Boolean);
        } else {
            return [];
        }
    };

    const [pageSize, setPageSize] = useState(20);
    const [newFileData, setFileData] = useState([]);
    const [filteredArrayData, setFilteredArrayData] = useState([]);
    const { processData } = useTableWorker();
    const isApplyingFilterRef = useRef(false);
    const lastReportRef = useRef({ report: null, view: null });

    const [sortType, setSortype] = useState();
    const [columnFilter, setColumnFilter] = useState([]);
    const [clearColumnFilter, setClearColumnFilter] = useState([]);
    const [filteredDataLength, setFiltredDataLength] = useState();
    const [exportData, setExportData] = useState(false);
    const [columnVisibility, setColumnVisibility] = useState([]);
    const [ssnDisplayMode, setSSNDisplayMode] = useState(0);
    const [columnOrder, setColumnOrder] = useState();
    const handleExportData = async (columnOrder) => {
        // Request full filtered data for export
        await processTableData(
            columnFilter,
            page,
            pageSize,
            reportTableData,
            sortType,
            true
        );
        setExportData(true);
        setColumnOrder(columnOrder);
    };

    const filtersLoadedRef = useRef(false);

    useEffect(() => {
        const currentReport = dropdownSelectedReport;
        const currentView = selectedReportView;

        // Check if we're switching reports or views
        const isReportOrViewChange =
            lastReportRef.current.report !== currentReport ||
            lastReportRef.current.view !== currentView;

        if (isReportOrViewChange) {
            filtersLoadedRef.current = false;
            lastReportRef.current = {
                report: currentReport,
                view: currentView,
            };
        }

        if (isApplyingFilterRef.current) return;
        if (filtersLoadedRef.current) return;

        if (!reportTableDisplayInformation?.length) return;

        if (selectedReportDetails?.TableDisplayInformation?.ColumnFilters) {
            const savedFilters = getColumnLevelFilters(
                selectedReportDetails?.TableDisplayInformation?.ColumnFilters
            );
            setColumnFilter(savedFilters);
            filtersLoadedRef.current = true;
        } else if (selectedReportDetails?.TableDisplayInformation) {
            setColumnFilter([]);
            filtersLoadedRef.current = true;
        }
    }, [
        selectedReportDetails?.TableDisplayInformation?.ColumnFilters,
        reportTableDisplayInformation,
        dropdownSelectedReport,
        selectedReportView,
    ]);

    const handleColumnVisibility = (columnVisibility) => {
        setColumnVisibility(columnVisibility);
    };
    const handleSsnDisplayMode = (displayMode) => {
        setSSNDisplayMode(displayMode);
    };
    useEffect(() => {
        if (exportData && exportRef.current) {
            exportRef.current.exportToExcel(); // Call the export function
            setExportData(false); // Reset the state to prevent repeated calls
        }
    }, [exportData]);

    // Process data with worker hook
    const processTableData = async (
        filters,
        currentPage,
        currentPageSize,
        data,
        sorting,
        needFullData = false
    ) => {
        if (!data || data.length === 0) {
            // Clear all state when no data is provided
            setFilteredArrayData([]);
            setFileData([]);
            setFiltredDataLength(0);
            return;
        }

        try {
            const result = await processData(
                data,
                sorting ? [sorting] : [],
                currentPage,
                currentPageSize,
                filters,
                needFullData
            );

            if (result.pageAdjusted && result.currentPage !== currentPage) {
                setPage((prev) => {
                    if (prev !== result.currentPage) {
                        return result.currentPage;
                    }
                    return prev;
                });
            }

            if (needFullData && result.filteredData) {
                setFilteredArrayData(result.filteredData);
            }
            setFileData(result.paginatedData || []);
            setFiltredDataLength(result.totalItems || 0);
        } catch (error) {
            // Handle worker termination gracefully
            if (
                error.message === "Worker terminated" ||
                error.message === "Worker not available"
            ) {
                setFilteredArrayData([]);
                setFileData([]);
                setFiltredDataLength(0);
            } else {
                console.error("Worker processing error:", error);
            }
        }
    };

    const prevReportDataLengthRef = useRef(reportTableData.length);

    useEffect(() => {
        if (reportTableData.length > 0) {
            const dataLengthChanged =
                prevReportDataLengthRef.current !== reportTableData.length;

            if (dataLengthChanged) {
                const totalPages = Math.ceil(reportTableData.length / pageSize);
                if (page > totalPages && totalPages > 0) {
                    setPage(totalPages);
                    prevReportDataLengthRef.current = reportTableData.length;
                    return;
                }
            }

            prevReportDataLengthRef.current = reportTableData.length;

            processTableData(
                columnFilter,
                page,
                pageSize,
                reportTableData,
                sortType
            );
        } else {
            setFilteredArrayData([]);
            setFileData([]);
            setFiltredDataLength(0);
            prevReportDataLengthRef.current = 0;
        }
    }, [columnFilter, page, pageSize, reportTableData, sortType]);

    const handlePageSelect = (item) => {
        setPage(item);
    };

    const getPageCount = () => {
        return Math.ceil(filteredDataLength / pageSize);
    };
    const getPagePerItems = (items) => {
        setPageSize(items);
        const newTotalPages = Math.ceil(filteredDataLength / items);
        const newPage =
            page > newTotalPages && newTotalPages > 0 ? newTotalPages : 1;
        setPage(newPage); // Reset to appropriate page when changing page size
    };
    const handleSortingChange = (item) => {
        if (!item || (item.id === null && item.desc === false)) {
            setSortype(undefined);
            return;
        }

        // Ensure the item has all required properties
        const normalizedItem = {
            id: item.id || item.accessorKey,
            desc: Boolean(item.desc),
            accessorKey: item.accessorKey || item.id,
            sortMethod: item.sortMethod || "",
        };
        setSortype(normalizedItem);
    };

    const handleColumnFilter = (newFilter) => {
        isApplyingFilterRef.current = true;
        setColumnFilter((prevFilters) => {
            let updatedFilters;

            // Clear all filters if newFilter is an empty array
            if (Array.isArray(newFilter) && newFilter.length === 0) {
                updatedFilters = [];
            } else {
                // Check if the filter for the given columnName already exists
                const existingFilterIndex = prevFilters?.findIndex(
                    (filter) => filter?.accessorKey === newFilter?.accessorKey
                );

                if (existingFilterIndex >= 0) {
                    // Replace the existing filter for this columnName
                    updatedFilters = [...prevFilters];
                    updatedFilters[existingFilterIndex] = newFilter;
                } else {
                    // Add the new filter
                    updatedFilters = [...prevFilters, newFilter];
                }
            }

            // Reset to page 1 when filters change to avoid invalid page scenarios
            const resetPage = 1;
            setPage(resetPage);

            // Process data with updated filters
            processTableData(
                updatedFilters,
                resetPage,
                pageSize,
                reportTableData,
                sortType
            );

            isApplyingFilterRef.current = false;
            return updatedFilters;
        });
    };

    useEffect(() => {
        if (clearColumnFilter.length >= 0 || clearColumnFilters === true) {
            setColumnFilter(clearColumnFilter);
        }
    }, [clearColumnFilter, clearColumnFilters]);

    const getSortDetails = (item) => {
        return columnsDatas.filter((obj) => obj.displayOrder === item);
    };

    const formattedHiddenFilters = hiddenFilterValues
        ?.map((obj) => ({
            label: obj.parameterName,
        }))
        .map((key) => key.label);

    const visibleColumnsInOrder = columnOrder?.filter(
        (col) => columnVisibility[col]
    );

    return (
        <>
            {exportData && (
                <ExportToCSV
                    ref={exportRef}
                    data={filteredArrayData}
                    defaultColumns={columnsDatas}
                    selectedReport={selectedReportDetails}
                    subviewMap={subviewMap}
                    columnFilters={columnFilter}
                    portalUserApiError={false}
                    reportLastFetched={reportLastFetched}
                    userEmail={userEmailID}
                    reportingPeriodValue={selectedReportingPeriod}
                    reportingStatusValue={selectedStatus}
                    selectedCoverageType={selectedCoverageType}
                    selectedViewDataType={dataViewType}
                    includeHistoricalLeaveValue={selectedIncludeHistoricalLeave}
                    periodScheduleTypeValue={selectedPeriodScheduleType}
                    leaveScheduleTypeValue={selectedLeaveScheduleType}
                    periodStatusValue={selectedPeriodStatus}
                    protectionStatusValue={selectedProtectionStatus}
                    disclaimer={disclaimer}
                    organizationsAccess={organizationsAccess}
                    ssnDisplayMode={ssnDisplayMode}
                    onExport={handleExportData}
                    organizationName={organizationName}
                    relatedOrganizationNames={relatedOrganizationNames}
                    selectedPolicyDivision={selectedPolicyDivision}
                    visibleColumnsInOrder={visibleColumnsInOrder}
                    selectedReportingGroup={selectedReportingGroup}
                    selectedPaymentStatus={selectedPaymentStatus}
                    //
                    enableCoverageType={enableCoverageType}
                    enableStatusFilter={enableStatusFilter}
                    enablePeriodScheduleTypeFilter={
                        enablePeriodScheduleTypeFilter
                    }
                    enableLeaveScheduleTypeFilter={
                        enableLeaveScheduleTypeFilter
                    }
                    enablePeriodStatusFilter={enablePeriodStatusFilter}
                    enableProtectionStatusFilter={enableProtectionStatusFilter}
                    enableIncludeHistoricalLeave={enableIncludeHistoricalLeave}
                    enableReportingPeriodFilter={enableReportingPeriodFilter}
                    enableReportGroupFilter={enableReportGroupFilter}
                    enablePolicyandDivisisonFilter={
                        enablePolicyandDivisisonFilter
                    }
                    enablePaymentStatus={enablePaymentStatus}
                    formattedHiddenFilters={formattedHiddenFilters}
                    reportViewType={dataViewType}
                    selectedApplyingReportingPeriod={
                        selectedApplyingReportingPeriod
                    }
                    enableApplyReportingPeriodFilter={
                        enableApplyReportingPeriodFilter
                    }
                />
            )}
            {dataViewType !== "Documents" && (
                <ReportTableView
                    selectedReportDetails={selectedReportDetails}
                    selectedReportView={selectedReportView}
                    dataViewType={dataViewType}
                    handleDropdownSelectedReport={handleDropdownSelectedReport}
                    dropdownSelectedReport={dropdownSelectedReport}
                    reportDropdownOptions={reportDropdownOptions}
                    allReportViewsData={allReportViewsData}
                    reportTableDisplayInformation={
                        reportTableDisplayInformation
                    }
                    reportTableData={reportTableData}
                    ReportTableData={newFileData}
                    filteredDataLength={filteredDataLength}
                    isFetchingReportData={isFetchingReportData}
                    isFetchingAccommodation={isFetchingAccommodation}
                    subviewName={subviewName}
                    isError={isError}
                    getPageCount={getPageCount()}
                    handlePageSelect={handlePageSelect}
                    isErrorReport={isErrorReport}
                    getPagePerItems={getPagePerItems}
                    handleColumnFilter={handleColumnFilter}
                    columnFilter={columnFilter}
                    organizationsAccess={organizationsAccess}
                    reportSortingColumnName={
                        selectedReportDetails?.TableDisplayInformation
                            ?.SortOrder?.columnDisplayOrderId
                            ? getSortDetails(
                                  selectedReportDetails?.TableDisplayInformation
                                      ?.SortOrder?.columnDisplayOrderId
                              )[0]?.id
                            : []
                    }
                    reportSortingColumnAccessorKey={
                        selectedReportDetails?.TableDisplayInformation
                            ?.SortOrder?.columnDisplayOrderId
                            ? getSortDetails(
                                  selectedReportDetails?.TableDisplayInformation
                                      ?.SortOrder?.columnDisplayOrderId
                              )[0]?.accessorKey
                            : []
                    }
                    reportSortingColumnDirection={
                        selectedReportDetails?.TableDisplayInformation
                            ?.SortOrder?.columnDisplayOrderId?.direction ===
                        "ASC"
                            ? false
                            : true
                    }
                    setClearColumnFilter={setClearColumnFilter}
                    originalDataLength={reportTableData?.length}
                    handleExportData={handleExportData}
                    handleColumnVisibility={handleColumnVisibility}
                    columnVisibility={columnVisibility}
                    handleSsnDisplayMode={handleSsnDisplayMode}
                    ssnDisplayMode={ssnDisplayMode}
                    reportDescription={reportDescription}
                    getSortDetails={handleSortingChange}
                    handleViewReportSave={handleViewReportSave}
                    loaderStatus={loaderStatus}
                    notificationStatus={notificationStatus}
                    allViewsData={allViewsData}
                    totalResults={totalResults}
                    retrievedCount={retrievedCount}
                    isPaginating={isPaginating}
                    handleViewReportUpdate={handleViewReportUpdate}
                    enableSaveButton={enableSaveButton}
                    setEnableSaveButton={setEnableSaveButton}
                    leavesStatus={leavesStatus}
                    setColumnFilter={setColumnFilter}
                    getColumnLevelFilters={getColumnLevelFilters}
                    isHighVolumeReport={HighVolumeReport}
                ></ReportTableView>
            )}
            {dataViewType === "Documents" && (
                <DocumentLibrary
                    dropdownSelectedReport={dropdownSelectedReport}
                    reportTableData={reportTableData}
                    columns={
                        selectedReportDetails?.TableDisplayInformation
                            ?.ColumnInformation ?? []
                    }
                    reportDescription={reportDescription}
                    dataViewType={dataViewType}
                    isFetchingReportData={isFetchingReportData}
                    isErrorReport={isErrorReport}
                    getSortDetails={handleSortingChange}
                >
                    {" "}
                </DocumentLibrary>
            )}
        </>
    );
}

ReportViewData.propTypes = {
    selectedReportDetails: PropTypes.object,
    selectedReportView: PropTypes.object,
    dataViewType: PropTypes.string,
    allReportViewsData: PropTypes.array,
    organizationName: PropTypes.string,
    relatedOrganizationNames: PropTypes.string,
    organizationsAccess: PropTypes.object,
    reportTableDisplayInformation: PropTypes.array,
    reportTableData: PropTypes.array,
    reportDropdownOptions: PropTypes.array,
    dropdownSelectedReport: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
    ]),
    handleDropdownSelectedReport: PropTypes.func,
    handleViewReportSave: PropTypes.func,
    handleViewReportUpdate: PropTypes.func,
    page: PropTypes.number,
    setPage: PropTypes.func,
    clearColumnFilters: PropTypes.bool,
    isFetchingReportData: PropTypes.bool,
    isFetchingAccommodation: PropTypes.bool,
    isErrorReport: PropTypes.bool,
    isError: PropTypes.bool,
    isPaginating: PropTypes.bool,
    loaderStatus: PropTypes.bool,
    enableSaveButton: PropTypes.bool,
    subviewName: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    reportDescription: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    disclaimer: PropTypes.string,
    reportLastFetched: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
    ]),
    userEmailID: PropTypes.string,
    totalResults: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    retrievedCount: PropTypes.number,
    selectedCoverageType: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
    ]),
    selectedReportingGroup: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
    ]),
    selectedPeriodScheduleType: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
    ]),
    selectedStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    selectedPeriodStatus: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
    ]),
    selectedProtectionStatus: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
    ]),
    selectedIncludeHistoricalLeave: PropTypes.array,
    selectedReportingPeriod: PropTypes.object,
    selectedPolicyDivision: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
    ]),
    selectedPaymentStatus: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
    ]),
    enableCoverageType: PropTypes.bool,
    enableStatusFilter: PropTypes.bool,
    enablePeriodScheduleTypeFilter: PropTypes.bool,
    enableLeaveScheduleTypeFilter: PropTypes.bool,
    enablePeriodStatusFilter: PropTypes.bool,
    enableProtectionStatusFilter: PropTypes.bool,
    enableIncludeHistoricalLeave: PropTypes.bool,
    enableReportingPeriodFilter: PropTypes.bool,
    enableReportGroupFilter: PropTypes.bool,
    enablePolicyandDivisisonFilter: PropTypes.bool,
    enablePaymentStatus: PropTypes.bool,
    hiddenFilterValues: PropTypes.array,
    notificationStatus: PropTypes.func,
    allViewsData: PropTypes.array,
    leavesStatus: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.array,
        PropTypes.bool,
    ]),
    subviewMap: PropTypes.string,
    enableApplyReportingPeriodFilter: PropTypes.bool,
    selectedApplyingReportingPeriod: PropTypes.array,
};

export default ReportViewData;
